import { useSyncExternalStore } from 'react';

/**
 * The AI Team hold + plan store (amendment 32 §6; specs 36 + 37). One
 * subscribable, localStorage-backed store so that holding an agent (or the whole
 * team) from the roster, from an agent page, or on a reload all show the same
 * state — and so a practice's plan (`?plan=core`) applies app-wide for the
 * session. Persisted so it survives a reload (specs 36 §5.1, §6).
 */

export interface TeamHoldState {
  held: boolean;
  since: string;
  by: string;
  waiting: number;
  /** "resume" | "tomorrow" | "1h" — the chosen duration. */
  duration: string;
}

export interface AgentHoldState {
  held: boolean;
  since: string;
  by: string;
  waiting: number;
}

/** A practice is on the full plan or on ELVA Core only (spec 37). */
export type ElvaPlan = 'full' | 'core';

/** The agents that come with ELVA Core (spec 37 §1). */
export const CORE_AGENTS = ['sms', 'email', 'recall', 'reminders'] as const;

/** The nine agent keys, in the roster's order (spec 36 §0). */
export const AI_TEAM_KEYS = [
  'receptionist', 'sms', 'email',
  'gap-filler', 'treatment', 'invisalign', 'ar', 'recall',
  'reminders',
] as const;

export function agentInPlan(key: string, plan: ElvaPlan): boolean {
  return plan === 'full' || (CORE_AGENTS as readonly string[]).includes(key);
}

interface Store {
  team: TeamHoldState;
  agents: Record<string, AgentHoldState>;
  plan: ElvaPlan;
  /** Set once from the URL (?plan=) per session; null = not yet decided. */
  planFromUrl: ElvaPlan | null;
}

const EMPTY: Store = {
  team: { held: false, since: '', by: '', waiting: 0, duration: 'resume' },
  agents: {},
  plan: 'full',
  planFromUrl: null,
};

const KEY = 'elva.aiTeamHold.v1';

function load(): Store {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (raw === null) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<Store>;
    return {
      team: { ...EMPTY.team, ...parsed.team },
      agents: parsed.agents ?? {},
      plan: parsed.plan ?? 'full',
      planFromUrl: parsed.planFromUrl ?? null,
    };
  } catch {
    return { ...EMPTY };
  }
}

const state: Store = load();
const listeners = new Set<() => void>();

function emit(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(state));
  } catch { /* preview sandboxes may block storage — hold state stays in memory */ }
  for (const l of listeners) l();
}

const subscribe = (fn: () => void): (() => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };

/* ── whole team ─────────────────────────────────────────────────────────── */

const getTeam = (): TeamHoldState => state.team;
export const teamHoldStore = { get: getTeam, subscribe };

export function holdTeam(opts: { by: string; since: string; waiting: number; duration: string }): void {
  state.team = { held: true, by: opts.by, since: opts.since, waiting: opts.waiting, duration: opts.duration };
  emit();
}
export function resumeTeam(): void {
  state.team = { ...state.team, held: false };
  emit();
}
export function useTeamHold(): TeamHoldState {
  return useSyncExternalStore(subscribe, getTeam, getTeam);
}

/* ── one agent at a time ────────────────────────────────────────────────── */

const DEFAULT_AGENT: AgentHoldState = { held: false, since: '', by: '', waiting: 0 };

export function getAgentHold(key: string): AgentHoldState {
  return state.agents[key] ?? DEFAULT_AGENT;
}

export function holdAgent(key: string, opts: { by: string; since: string; waiting: number }): void {
  state.agents = { ...state.agents, [key]: { held: true, by: opts.by, since: opts.since, waiting: opts.waiting } };
  emit();
}
export function resumeAgent(key: string): void {
  state.agents = { ...state.agents, [key]: { ...DEFAULT_AGENT } };
  emit();
}

/** The keys of the agents currently held on their own (not the whole team). */
export function heldAgentKeys(): string[] {
  return Object.keys(state.agents).filter((k) => state.agents[k]?.held);
}

/** Subscribe to one agent's hold state. */
export function useAgentHold(key: string): AgentHoldState {
  const get = (): AgentHoldState => getAgentHold(key);
  return useSyncExternalStore(subscribe, get, get);
}

/** Subscribe to the whole store (for the roster, which reads every agent). */
export function useHoldStore(): Store {
  const get = (): Store => state;
  return useSyncExternalStore(subscribe, get, get);
}

/* ── plan ───────────────────────────────────────────────────────────────── */

const getPlan = (): ElvaPlan => state.plan;

/** Apply the plan from the URL (?plan=core|full) once, for the session. */
export function setPlanFromUrl(plan: ElvaPlan): void {
  if (state.planFromUrl === plan && state.plan === plan) return;
  state.plan = plan;
  state.planFromUrl = plan;
  emit();
}

export function usePlan(): ElvaPlan {
  return useSyncExternalStore(subscribe, getPlan, getPlan);
}
